import { useMemo, useContext, Context } from "react"

// Types génériques
type Selector<TState, TSelected> = (state: TState) => TSelected
type EqualityFn<T> = (a: T, b: T) => boolean

/**
 * Hook générique pour sélectionner une partie du state d'un contexte
 * Version simplifiée qui fonctionne avec n'importe quel contexte ayant une propriété 'state'
 */
export const useContextSelector = <TSelected>(
  contextValue: any,
  selector: Selector<any, TSelected>
): TSelected => {
  if (!contextValue || !contextValue.state) {
    throw new Error('Context value must have a state property')
  }

  return useMemo(() => selector(contextValue.state), [
    contextValue.state,
    selector
  ])
}

/**
 * Hook générique avec fonction d'égalité personnalisée
 */
export const useContextSelectorWithEquality = <TSelected>(
  contextValue: any,
  selector: Selector<any, TSelected>,
  equalityFn: EqualityFn<TSelected> = Object.is
): TSelected => {
  if (!contextValue || !contextValue.state) {
    throw new Error('Context value must have a state property')
  }

  const previousValue = useMemo(() => selector(contextValue.state), [
    contextValue.state,
    selector
  ])

  return useMemo(() => {
    const currentValue = selector(contextValue.state)
    return equalityFn(previousValue, currentValue) ? previousValue : currentValue
  }, [
    contextValue.state,
    selector,
    equalityFn,
    previousValue
  ])
}

/**
 * Factory pour créer des hooks spécifiques à un contexte
 * Version simplifiée
 */
export const createContextSelectorHooks = <TContext, TState>(
  context: Context<TContext | undefined>,
  contextName?: string
) => {
  const useSelector = <TSelected>(
    selector: Selector<TState, TSelected>
  ): TSelected => {
    const contextValue = useContext(context)

    if (contextValue === undefined) {
      const errorMessage = contextName
        ? `useSelector must be used within a ${contextName}Provider`
        : 'useSelector must be used within the appropriate Provider'
      throw new Error(errorMessage)
    }

    return useContextSelector(contextValue, selector)
  }

  const useSelectorWithEquality = <TSelected>(
    selector: Selector<TState, TSelected>,
    equalityFn: EqualityFn<TSelected> = Object.is
  ): TSelected => {
    const contextValue = useContext(context)

    if (contextValue === undefined) {
      const errorMessage = contextName
        ? `useSelectorWithEquality must be used within a ${contextName}Provider`
        : 'useSelectorWithEquality must be used within the appropriate Provider'
      throw new Error(errorMessage)
    }

    return useContextSelectorWithEquality(contextValue, selector, equalityFn)
  }

  return {
    useSelector,
    useSelectorWithEquality
  }
}

// Fonctions d'égalité utilitaires
export const equalityFunctions = {
  shallowEqual: <T extends Record<string, any>>(a: T, b: T): boolean => {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)

    if (keysA.length !== keysB.length) return false

    for (const key of keysA) {
      if (a[key] !== b[key]) return false
    }

    return true
  },

  shallowEqualArray: <T>(a: T[], b: T[]): boolean => {
    if (a.length !== b.length) return false

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return false
    }

    return true
  },

  deepEqual: <T>(a: T, b: T): boolean => {
    return JSON.stringify(a) === JSON.stringify(b)
  }
}
